/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { useMemo, useState } from 'react'
import { useIntl } from 'react-intl'

import {
  EuiBasicTable,
  EuiButton,
  EuiFlexGroup,
  EuiFlexItem,
  EuiSkeletonText,
  EuiSpacer,
  EuiText,
  useEuiTheme,
} from '@elastic/eui'
import type { EuiBasicTableProps, CriteriaWithPagination } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import type { AnyProject } from '@modules/project-api/types'

import { useProjectCpsContext } from './ProjectCpsContext'
import { useProjectsTableColumns } from './useProjectsTableColumns'
import { ProjectCpsSearchbar } from './ProjectCpsSearchbar'
import { ProjectsTableEmptyState } from './ProjectsTableEmptyState'

import type { ReactNode } from 'react'

type Props = {
  items: AnyProject[]
  selectedProjects: AnyProject[]
  onSelectionChange: (selected: AnyProject[]) => void
  onSelectAll: () => void
  onSelectNone: () => void
  selection: EuiBasicTableProps<AnyProject>['selection']
  rowProps?: (project: AnyProject) => Record<string, any>
  showSearch?: boolean
  searchPlaceholder?: string
  headerContent?: ReactNode
  rightSideNote?: ReactNode
  showingTextType?: 'default' | 'linked'
  showSelectAllNone?: boolean
  actionButtons?: ReactNode
}

const ProjectsTable: React.FunctionComponent<Props> = ({
  items,
  selectedProjects,
  onSelectAll,
  onSelectNone,
  selection,
  rowProps,
  showSearch = true,
  searchPlaceholder = 'Search',
  headerContent,
  rightSideNote: rightSideNote,
  showingTextType = 'default',
  showSelectAllNone = true,
  actionButtons,
}) => {
  const { formatMessage } = useIntl()
  const [pageIndex, setPageIndex] = useState(0)
  const [pageSize, setPageSize] = useState(20)
  const [filteredProjectIds, setFilteredProjectIds] = useState<string[] | null>(null)
  const { euiTheme } = useEuiTheme()
  const { useListRegionsQuery } = useProjectCpsContext()

  const regionsQuery = useListRegionsQuery()
  const { data: regions, isLoading: regionsIsLoading } = regionsQuery

  const filteredItems = useMemo(() => {
    if (!filteredProjectIds) {
      return items
    }

    return items.filter((item) => filteredProjectIds.includes(item.id))
  }, [filteredProjectIds, items])

  const totalItems = filteredItems.length
  const startIndex = pageIndex * pageSize
  const endIndex = Math.min(startIndex + pageSize, totalItems)
  const currentPageItems = filteredItems.slice(startIndex, endIndex)
  const currentPageItemCount = currentPageItems.length

  // Get columns with custom checkbox selection logic
  const columns = useProjectsTableColumns({
    selection,
    currentPageItems,
    regions,
  })

  const pagination = {
    pageIndex,
    pageSize,
    totalItemCount: totalItems,
    pageSizeOptions: [20, 50],
    showPerPageOptions: true,
  }

  const onTableChange = ({ page }: CriteriaWithPagination<AnyProject>) => {
    setPageIndex(page.index)
    setPageSize(page.size)
  }

  const hasSelection = selectedProjects.length > 0

  // Render select all/none buttons
  const renderSelectButtons = () => {
    if (!showSelectAllNone || !hasSelection) {
      return null
    }

    return (
      <React.Fragment>
        <EuiFlexItem grow={false}>
          <EuiButton
            onClick={onSelectAll}
            size='s'
            color='neutral'
            data-test-subj='cps-projectsTable-selectAll'
          >
            <FormattedMessage
              id='linkedProjects.selectAll'
              defaultMessage='Select all {total}'
              values={{ total: totalItems }}
            />
          </EuiButton>
        </EuiFlexItem>
        <EuiFlexItem grow={false}>
          <EuiButton
            onClick={onSelectNone}
            size='s'
            color='neutral'
            data-test-subj='cps-projectsTable-selectNone'
          >
            <FormattedMessage id='linkedProjects.selectNone' defaultMessage='Select none' />
          </EuiButton>
        </EuiFlexItem>
      </React.Fragment>
    )
  }

  if (regionsIsLoading) {
    return <EuiSkeletonText />
  }

  return (
    <EuiFlexGroup direction='column' gutterSize='s'>
      {showSearch && (
        <ProjectCpsSearchbar
          projects={items}
          regions={regions}
          onProjectsFilter={setFilteredProjectIds}
          searchPlaceholder={searchPlaceholder}
        />
      )}

      {headerContent && <EuiFlexItem>{headerContent}</EuiFlexItem>}

      <EuiFlexGroup
        gutterSize='s'
        alignItems='center'
        justifyContent={hasSelection ? undefined : 'spaceBetween'}
      >
        {/* Left side: action buttons and/or selection count or showing text */}
        {hasSelection ? (
          <React.Fragment>
            {actionButtons && (
              <EuiFlexItem
                grow={false}
                style={{
                  paddingRight: euiTheme.size.base,
                  borderRight: `1px solid ${euiTheme.colors.borderBasePlain}`,
                }}
              >
                {actionButtons}
              </EuiFlexItem>
            )}
            <EuiFlexItem
              grow={false}
              style={{ paddingLeft: actionButtons ? euiTheme.size.xs : undefined }}
            >
              <EuiText size='s' color='subdued' style={{ minHeight: euiTheme.size.xl }}>
                <FormattedMessage
                  id='linkedProjects.selectedCount'
                  defaultMessage='{count} of {total} selected'
                  values={{
                    count: selectedProjects.length,
                    total: totalItems,
                  }}
                />
              </EuiText>
            </EuiFlexItem>
          </React.Fragment>
        ) : (
          <EuiFlexItem grow={false}>
            <EuiText size='s' color='subdued' style={{ minHeight: euiTheme.size.xl }}>
              {showingTextType === 'linked' ? (
                <FormattedMessage
                  id='linkedProjects.showingCount'
                  defaultMessage='Showing linked projects {count} of {total}'
                  values={{
                    count: currentPageItemCount,
                    total: totalItems,
                  }}
                />
              ) : (
                <FormattedMessage
                  id='cps.selectProjects.showing'
                  defaultMessage='Showing {start}-{end} of {total}'
                  values={{
                    start: totalItems > 0 ? startIndex + 1 : 0,
                    end: endIndex,
                    total: totalItems,
                  }}
                />
              )}
            </EuiText>
          </EuiFlexItem>
        )}

        {renderSelectButtons()}
        {rightSideNote}
      </EuiFlexGroup>

      <EuiSpacer size='m' />

      <EuiFlexItem>
        <EuiBasicTable<AnyProject>
          tableLayout='auto'
          items={currentPageItems}
          itemId={(item) => item.id}
          columns={columns}
          rowHeader='name'
          tableCaption={formatMessage({
            id: 'linkedProjects.tableCaption',
            defaultMessage: 'Projects table',
          })}
          pagination={pagination}
          onChange={onTableChange}
          rowProps={rowProps}
          responsiveBreakpoint='l'
          noItemsMessage={<ProjectsTableEmptyState hasFilters={filteredProjectIds !== null} />}
        />
      </EuiFlexItem>
    </EuiFlexGroup>
  )
}

export default ProjectsTable
